import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { ClipboardCheck, Plus, Search, CheckCircle, XCircle, ArrowLeft, Package, MapPin, Trash2 } from 'lucide-react';
import BarcodeScanInput from '../components/BarcodeScanInput';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const StockCount = () => {
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, prepare, count
  const [activeCount, setActiveCount] = useState(null);
  const [countItems, setCountItems] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');

  // Prepare form
  const [prepName, setPrepName] = useState('');
  const [prepLocation, setPrepLocation] = useState('Default Location');
  const [prepScope, setPrepScope] = useState('all');
  const [prepCategory, setPrepCategory] = useState('');
  const [preparing, setPreparing] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      const data = await api.request('/api/stock-counts');
      setCounts(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const createCount = async () => {
    setPreparing(true);
    try {
      const res = await api.request('/api/stock-counts', {
        method: 'POST',
        body: JSON.stringify({
          name: prepName || undefined,
          location: prepLocation,
          scope: prepScope,
          category_filter: prepCategory,
        }),
      });
      await loadCounts();
      openCount(res.id);
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setPreparing(false); }
  };

  const openCount = async (id) => {
    try {
      const data = await api.request(`/api/stock-counts/${id}`);
      setActiveCount(data);
      setCountItems(data.items || []);
      setView('count');
    } catch (err) { alert('Failed to load: ' + err.message); }
  };

  const updateItemCount = (variantId, value) => {
    setCountItems(prev => prev.map(item =>
      item.variant_id === variantId ? {
        ...item,
        counted_qty: value === '' ? null : parseInt(value),
        discrepancy: value === '' ? null : parseInt(value) - item.system_qty,
        status: value === '' ? 'pending' : 'counted'
      } : item
    ));
  };

  const saveProgress = async () => {
    const updated = countItems.filter(i => i.status === 'counted').map(i => ({
      variant_id: i.variant_id, counted_qty: i.counted_qty
    }));
    if (updated.length === 0) return;
    try {
      await api.request(`/api/stock-counts/${activeCount.id}/count`, {
        method: 'PUT',
        body: JSON.stringify({ items: updated }),
      });
      alert('Progress saved');
    } catch (err) { alert('Save failed: ' + err.message); }
  };

  const completeCount = async () => {
    const uncounted = countItems.filter(i => i.status !== 'counted').length;
    if (uncounted > 0 && !window.confirm(`${uncounted} items not counted. Complete anyway?`)) return;
    const discrepancies = countItems.filter(i => i.discrepancy && i.discrepancy !== 0);
    if (discrepancies.length > 0 && !window.confirm(`${discrepancies.length} items have discrepancies. Apply adjustments to inventory?`)) return;
    try {
      const res = await api.request(`/api/stock-counts/${activeCount.id}/complete`, {
        method: 'PUT',
        body: JSON.stringify({ apply_adjustments: true }),
      });
      alert(`Count completed! ${res.adjustments_made} adjustments made (${res.shrinkage_items} shrinkage, ${res.surplus_items} surplus)`);
      setView('list');
      loadCounts();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const deleteCount = async (id) => {
    if (!window.confirm('Delete this stock count?')) return;
    try {
      await api.request(`/api/stock-counts/${id}`, { method: 'DELETE' });
      loadCounts();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const filtered = countItems.filter(i => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (i.product_name || '').toLowerCase().includes(q) || (i.sku || '').toLowerCase().includes(q);
  });

  const statusBadge = (status) => {
    const map = {
      prepared: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      completed: 'bg-green-100 text-green-700',
    };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-gray-100'}`}>{status?.replace('_', ' ')}</span>;
  };

  // PREPARE VIEW
  if (view === 'prepare') return (
    <div className="space-y-6" data-testid="stock-count-prepare">
      <div className="flex items-center gap-3">
        <button onClick={() => setView('list')} className="p-2 hover:bg-muted rounded"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="text-2xl font-bold">Prepare Count</h1>
      </div>
      <div className="bg-card border rounded-lg p-6 max-w-2xl space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Count Name</label>
          <input type="text" placeholder="e.g. Monthly Count - April 2026" value={prepName} onChange={e => setPrepName(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-lg text-sm" data-testid="count-name" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1"><MapPin className="inline h-4 w-4 mr-1" />Location</label>
          <input type="text" value={prepLocation} onChange={e => setPrepLocation(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-lg text-sm" data-testid="count-location" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Scope</label>
          <select value={prepScope} onChange={e => setPrepScope(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-background" data-testid="count-scope">
            <option value="all">All Products</option>
            <option value="category">By Category</option>
          </select>
        </div>
        {prepScope === 'category' && (
          <div>
            <label className="block text-sm font-medium mb-1">Category Filter</label>
            <input type="text" placeholder="e.g. Supplies" value={prepCategory} onChange={e => setPrepCategory(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg text-sm" data-testid="count-category" />
          </div>
        )}
        <button onClick={createCount} disabled={preparing}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium"
          data-testid="start-count-btn">
          {preparing ? 'Preparing...' : 'Start Count'}
        </button>
      </div>
    </div>
  );

  // COUNT VIEW
  if (view === 'count' && activeCount) {
    const countedCount = countItems.filter(i => i.status === 'counted').length;
    const discrepancyCount = countItems.filter(i => i.discrepancy && i.discrepancy !== 0).length;
    return (
      <div className="space-y-4" data-testid="stock-count-active">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('list'); loadCounts(); }} className="p-2 hover:bg-muted rounded"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h1 className="text-xl font-bold">{activeCount.name}</h1>
              <p className="text-sm text-muted-foreground">{activeCount.location} | {countItems.length} items</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{countedCount}</span> / {countItems.length} counted
              {discrepancyCount > 0 && <span className="ml-2 text-amber-600">{discrepancyCount} discrepancies</span>}
            </div>
            <button onClick={saveProgress} className="px-4 py-2 border rounded-lg hover:bg-muted text-sm" data-testid="save-progress-btn">Save Progress</button>
            <button onClick={completeCount} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium" data-testid="complete-count-btn">
              <CheckCircle className="inline h-4 w-4 mr-1" />Complete Count
            </button>
          </div>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" placeholder="Search products..." value={searchFilter} onChange={e => setSearchFilter(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-input rounded-lg text-sm" data-testid="count-search" />
        </div>

        {/* Barcode scan — each scan bumps the matched item's Counted Qty by 1 */}
        <div className="max-w-md" data-testid="count-barcode-wrapper">
          <label className="block text-xs text-muted-foreground mb-1">Scan to count (+1 per scan)</label>
          <BarcodeScanInput
            onScan={(product) => {
              const sku = (product?.sku || '').toLowerCase();
              const bc = (product?.barcode || '').toLowerCase();
              const target = countItems.find(it => {
                const itSku = (it.sku || '').toLowerCase();
                return itSku && (itSku === sku || itSku === bc);
              });
              if (!target) {
                alert(`SKU ${product?.sku || '?'} isn't in this count.`);
                return;
              }
              const next = (Number(target.counted_qty) || 0) + 1;
              updateItemCount(target.variant_id, String(next));
            }}
            onMiss={(c) => alert(`Barcode "${c}" not found.`)}
            placeholder="Scan item to count…"
          />
        </div>

        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full" data-testid="count-items-table">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Product</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">SKU</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">System Qty</th>
                <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase w-32">Counted Qty</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Discrepancy</th>
                <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(item => (
                <tr key={item.variant_id} className={`hover:bg-muted/20 ${item.discrepancy && item.discrepancy !== 0 ? 'bg-amber-50/50' : ''}`}>
                  <td className="px-4 py-2 text-sm font-medium">{item.product_name}</td>
                  <td className="px-4 py-2 text-sm text-muted-foreground font-mono">{item.sku || '—'}</td>
                  <td className="px-4 py-2 text-sm text-right tabular-nums">{item.system_qty}</td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="number" min="0"
                      value={item.counted_qty ?? ''}
                      onChange={e => updateItemCount(item.variant_id, e.target.value)}
                      placeholder="—"
                      className="w-20 px-2 py-1 border border-input rounded text-sm text-center mx-auto"
                      data-testid={`count-input-${item.variant_id}`}
                    />
                  </td>
                  <td className="px-4 py-2 text-sm text-right tabular-nums">
                    {item.discrepancy != null ? (
                      <span className={item.discrepancy < 0 ? 'text-red-600 font-semibold' : item.discrepancy > 0 ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>
                        {item.discrepancy > 0 ? '+' : ''}{item.discrepancy}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {item.status === 'counted' ? <CheckCircle className="h-4 w-4 text-green-500 inline" /> :
                     item.status === 'skipped' ? <XCircle className="h-4 w-4 text-gray-400 inline" /> :
                     <span className="text-xs text-muted-foreground">Pending</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="space-y-6" data-testid="stock-count-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ClipboardCheck className="h-6 w-6" /> Stock Count</h1>
          <p className="text-sm text-muted-foreground mt-1">Reconcile physical inventory with system records</p>
        </div>
        <button onClick={() => setView('prepare')}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium"
          data-testid="new-count-btn">
          <Plus className="h-4 w-4" /> Prepare Count
        </button>
      </div>

      <div className="bg-card border rounded-lg overflow-hidden">
        <table className="w-full" data-testid="counts-list-table">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Name</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Location</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Items</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Counted</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Created</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : counts.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                No stock counts yet. Click "Prepare Count" to start.
              </td></tr>
            ) : counts.map(sc => (
              <tr key={sc.id} className="hover:bg-muted/20">
                <td className="px-4 py-3 font-medium text-sm cursor-pointer hover:text-primary" onClick={() => openCount(sc.id)}>{sc.name}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{sc.location}</td>
                <td className="px-4 py-3 text-sm text-center">{sc.total_items}</td>
                <td className="px-4 py-3 text-sm text-center">{sc.counted_items || 0}</td>
                <td className="px-4 py-3 text-center">{statusBadge(sc.status)}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{fmtDate(sc.created_at)}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    {sc.status !== 'completed' && (
                      <button onClick={() => openCount(sc.id)} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                        {sc.status === 'prepared' ? 'Start' : 'Continue'}
                      </button>
                    )}
                    <button onClick={() => deleteCount(sc.id)} className="p-1 text-muted-foreground hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockCount;
